import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { AvatarStack } from "@/components/common/Avatar";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { dateFmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Pencil, Trash2, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

export default function MeetingNotes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, me } = useApp();
  const [open, setOpen] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [form, setForm] = useState({});
  const [actionText, setActionText] = useState({});
  const [search, setSearch] = useState("");

  const { data: notes = [], isLoading } = useQuery({ queryKey: ["notes", id], queryFn: () => get(`/pm/projects/${id}/meeting-notes`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notes", id] });

  const create = useMutation({
    mutationFn: (body) => post(`/pm/projects/${id}/meeting-notes`, body),
    onSuccess: () => { invalidate(); setOpen(false); setForm({}); toast.success("Note added"); },
  });
  const update = useMutation({
    mutationFn: ({ nid, body }) => patch(`/pm/meeting-notes/${nid}`, body),
    onSuccess: () => { invalidate(); setEditNote(null); toast.success("Note updated"); },
  });
  const remove = useMutation({ mutationFn: (nid) => del(`/pm/meeting-notes/${nid}`), onSuccess: () => { invalidate(); toast.success("Note deleted"); } });
  const addAction = useMutation({
    mutationFn: ({ nid, text }) => post(`/pm/meeting-notes/${nid}/action-items`, { text }),
    onSuccess: () => invalidate(),
  });
  const toggleAction = useMutation({
    mutationFn: ({ nid, aid, done }) => patch(`/pm/meeting-notes/${nid}/action-items/${aid}`, { done }),
    onSuccess: () => invalidate(),
  });
  const convertAction = useMutation({
    mutationFn: ({ nid, aid }) => post(`/pm/meeting-notes/${nid}/action-items/${aid}/convert`, { assigned_by: me?.id }),
    onSuccess: (ticket) => { invalidate(); toast.success("Converted to task"); navigate(`/pm/projects/${id}/items/${ticket.id}`); },
  });

  const filteredNotes = useMemo(() => {
    if (!search) return notes;
    const s = search.toLowerCase();
    return notes.filter((n) => n.title?.toLowerCase().includes(s) || n.body?.toLowerCase().includes(s));
  }, [notes, search]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <SearchInput testid="notes-search" placeholder="Search notes" value={search} onChange={setSearch} />
        <Button size="sm" data-testid="new-note-btn" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />New Note</Button>
      </div>
      {notes.length === 0 ? <EmptyState icon={FileText} title="No meeting notes" /> :
       filteredNotes.length === 0 ? <EmptyState icon={FileText} title="No notes match your search" /> : (
        <div className="space-y-3">
          {filteredNotes.map((n) => (
            <div key={n.id} data-testid={`note-${n.id}`} className="group rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-sm font-semibold">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{dateFmt(n.date)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <AvatarStack users={(n.attendees || []).map((a) => umap[a]).filter(Boolean)} size="sm" />
                  {can("pm", "edit") && (
                    <div className="hidden items-center gap-0.5 group-hover:flex">
                      <button onClick={() => { setEditNote(n.id); setForm(n); }} className="rounded p-1 text-muted-foreground hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
                      {can("pm", "delete") && (
                        <ConfirmDelete testid={`del-note-${n.id}`} title="Delete this meeting note?"
                          trigger={<button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                          onConfirm={() => remove.mutate(n.id)} />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-2 text-sm text-foreground">{n.body}</p>
              <p className="mt-2 text-xs text-muted-foreground">Last edited by {umap[n.updated_by]?.name || "—"}</p>

              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-1.5 text-xs font-semibold text-muted-foreground">Action Items</div>
                <div className="space-y-1">
                  {(n.action_items || []).map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={a.done} onCheckedChange={(v) => toggleAction.mutate({ nid: n.id, aid: a.id, done: !!v })} />
                      <span className={a.done ? "flex-1 text-muted-foreground line-through" : "flex-1"}>{a.text}</span>
                      {a.converted_ticket_id ? (
                        <button onClick={() => navigate(`/pm/projects/${id}/items/${a.converted_ticket_id}`)} className="text-xs text-primary hover:underline">View ticket</button>
                      ) : can("pm", "create") && (
                        <button data-testid={`convert-action-${a.id}`} onClick={() => convertAction.mutate({ nid: n.id, aid: a.id })}
                          title="Convert to task" className="text-muted-foreground hover:text-primary"><ArrowRightCircle className="h-4 w-4" /></button>
                      )}
                    </div>
                  ))}
                </div>
                {can("pm", "create") && (
                  <div className="mt-2 flex gap-2">
                    <Input placeholder="Add action item and press Enter" className="h-8 text-sm"
                      value={actionText[n.id] || ""} onChange={(e) => setActionText((s) => ({ ...s, [n.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter" && actionText[n.id]) { addAction.mutate({ nid: n.id, text: actionText[n.id] }); setActionText((s) => ({ ...s, [n.id]: "" })); } }} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-note-modal">
          <DialogHeader><DialogTitle>New Meeting Note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="note-title" className="mt-1" onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div><Label>Date</Label><Input type="date" className="mt-1" onChange={(e) => setForm((f) => ({ ...f, date: new Date(e.target.value).toISOString() }))} /></div>
            <div><Label>Notes</Label><Textarea data-testid="note-body" rows={4} className="mt-1" onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-note" onClick={() => { if (!form.title) return toast.error("Title required"); create.mutate({ ...form, updated_by: me?.id, attendees: [] }); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editNote} onOpenChange={(v) => !v && setEditNote(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-note-modal">
          <DialogHeader><DialogTitle>Edit Meeting Note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input className="mt-1" value={form.title || ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
            <div><Label>Notes</Label><Textarea rows={4} className="mt-1" value={form.body || ""} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditNote(null)}>Cancel</Button>
            <Button onClick={() => update.mutate({ nid: editNote, body: { title: form.title, body: form.body, updated_by: me?.id } })}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
